import { Box, Flex, Grid, Text, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { LuChevronRight as ChevronRight, LuMapPin as MapPin, LuTruck as Truck, LuUsers as Users } from 'react-icons/lu'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { useChartTheme } from '../../../hooks/useChartTheme'
import { brand, themeColors } from '../../../theme'
import { routeAnalytics } from '../../../data/mockEstadisticas'
import CustomTooltip from '../components/CustomTooltip'
import ChartCard from '../components/ChartCard'

const MotionBox = motion.create(Box)

export default function DetalleRutasPage() {
  const { axis, grid } = useChartTheme()
  const colors = themeColors
  const navigate = useNavigate()

  const chartData = routeAnalytics.map((r) => ({
    ruta: `${r.origen.split(' ')[0]} → ${r.destino.split(' ')[0]}`,
    traslados: r.trasladosCompletados,
    tonelaje: r.tonelajeTransportado,
  }))

  return (
    <VStack gap={6} alignItems="stretch">
      {/* Breadcrumb */}
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Flex gap={2} alignItems="center" mb={2}>
          <Text fontSize="sm" color={brand.green} cursor="pointer" _hover={{ textDecoration: 'underline' }} onClick={() => navigate('/app/estadisticas')}>
            Estadisticas
          </Text>
          <ChevronRight size={14} color={colors.textMuted} />
          <Text fontSize="sm" color={colors.text} fontWeight="600">
            Rutas
          </Text>
        </Flex>
        <Text fontSize="2xl" fontWeight="bold" color={colors.text}>
          Detalle por Ruta
        </Text>
        <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.2em" fontWeight="bold" color={colors.textMuted} mt={1}>
          {routeAnalytics.length} rutas activas
        </Text>
      </MotionBox>

      {/* Route Cards */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }} gap={4}>
        {routeAnalytics.map((route, index) => (
          <MotionBox
            key={route.id}
            bg={colors.bgCard}
            border="1px solid"
            borderColor={colors.border}
            borderRadius="2xl"
            p={5}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            boxShadow="none"
          >
            <Flex alignItems="center" gap={2} mb={1}>
              <MapPin size={16} color={brand.green} />
              <Text fontSize="md" fontWeight="bold" color={colors.text}>
                {route.origen}
              </Text>
            </Flex>
            <Text fontSize="xs" color={colors.textMuted} mb={1}>
              → {route.destino} ({route.distanciaKm} km)
            </Text>
            <Box
              display="inline-block"
              bg={`${brand.green}22`}
              color={brand.green}
              px={2}
              py={0.5}
              borderRadius="md"
              fontSize="xs"
              fontWeight="600"
              mb={3}
            >
              {route.mineralPrincipal}
            </Box>
            <Grid templateColumns="repeat(3, 1fr)" gap={3}>
              <Box>
                <Flex alignItems="center" gap={1} mb={1}>
                  <Truck size={12} color={colors.textMuted} />
                  <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.1em" color={colors.textMuted}>
                    Viajes
                  </Text>
                </Flex>
                <Text fontSize="lg" fontWeight="bold" color={colors.text}>
                  {route.trasladosCompletados}
                </Text>
              </Box>
              <Box>
                <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.1em" color={colors.textMuted} mb={1}>
                  Tonelaje
                </Text>
                <Text fontSize="lg" fontWeight="bold" color={colors.text}>
                  {(route.tonelajeTransportado / 1000).toFixed(1)}k
                </Text>
              </Box>
              <Box>
                <Flex alignItems="center" gap={1} mb={1}>
                  <Users size={12} color={colors.textMuted} />
                  <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.1em" color={colors.textMuted}>
                    Proveed.
                  </Text>
                </Flex>
                <Text fontSize="lg" fontWeight="bold" color={colors.text}>
                  {route.proveedoresActivos}
                </Text>
              </Box>
            </Grid>
          </MotionBox>
        ))}
      </Grid>

      {/* Comparative Chart */}
      <ChartCard title="Volumen por Ruta" subtitle="Traslados y tonelaje transportado" delay={0.3}>
        <Box h="320px">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid stroke={grid.stroke} strokeDasharray={grid.strokeDasharray} />
              <XAxis dataKey="ruta" stroke={axis.stroke} tick={{ ...axis.tick, fontSize: 10 }} />
              <YAxis yAxisId="left" stroke={axis.stroke} tick={axis.tick} />
              <YAxis yAxisId="right" orientation="right" stroke={axis.stroke} tick={axis.tick} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip formatter={(v, name) => name === 'Tonelaje (TM)' ? `${v.toLocaleString()} TM` : v.toLocaleString()} />} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "'Instrument Sans', sans-serif" }} />
              <Bar yAxisId="left" dataKey="traslados" name="Traslados" fill={brand.green} radius={[4, 4, 0, 0]} barSize={24} />
              <Bar yAxisId="right" dataKey="tonelaje" name="Tonelaje (TM)" fill="#3d6fa8" radius={[4, 4, 0, 0]} barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </ChartCard>

      {/* Summary Table */}
      <MotionBox
        bg={colors.bgCard}
        border="1px solid"
        borderColor={colors.border}
        borderRadius="2xl"
        overflow="hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.35 }}
        boxShadow="none"
      >
        <Box p={6} borderBottom="1px solid" borderColor={colors.border}>
          <Text fontSize="lg" fontWeight="bold" color={colors.text}>
            Resumen de Rutas
          </Text>
        </Box>
        <Box overflowX="auto">
          <Box as="table" width="100%" textAlign="left">
            <Box as="thead">
              <Box as="tr" bg={colors.bgHover}>
                {['Origen', 'Destino', 'Distancia', 'Traslados', 'Tonelaje', 'Proveedores', 'Mineral'].map((h) => (
                  <Box as="th" key={h} px={6} py={4} fontSize="10px" textTransform="uppercase" letterSpacing="0.2em" fontWeight="bold" color={colors.textMuted}>
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box as="tbody">
              {routeAnalytics.map((r) => (
                <Box as="tr" key={r.id} borderBottom="1px solid" borderColor={colors.border} _hover={{ bg: colors.bgHover }}>
                  <Box as="td" px={6} py={4}><Text fontSize="sm" color={colors.text}>{r.origen}</Text></Box>
                  <Box as="td" px={6} py={4}><Text fontSize="sm" color={colors.text}>{r.destino}</Text></Box>
                  <Box as="td" px={6} py={4}><Text fontSize="sm" color={colors.text}>{r.distanciaKm} km</Text></Box>
                  <Box as="td" px={6} py={4}><Text fontSize="sm" color={colors.text}>{r.trasladosCompletados}</Text></Box>
                  <Box as="td" px={6} py={4}><Text fontSize="sm" color={colors.text}>{r.tonelajeTransportado.toLocaleString()} TM</Text></Box>
                  <Box as="td" px={6} py={4}><Text fontSize="sm" color={colors.text}>{r.proveedoresActivos}</Text></Box>
                  <Box as="td" px={6} py={4}>
                    <Box display="inline-block" px={2} py={0.5} borderRadius="md" fontSize="xs" fontWeight="600" bg={`${brand.green}22`} color={brand.green}>
                      {r.mineralPrincipal}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </MotionBox>
    </VStack>
  )
}
